import { useEffect, useState } from "react";

/**
 * Lazily fetches a single installation photo's full image (base64) on demand
 * via the service's getImage(id) call, and returns a `data:` URL.
 *
 * Keeping image bytes out of the list response and loading them per-image here
 * avoids pulling every full-resolution photo at once (which made the page slow).
 *
 * @param {object} service  InstallationPhotoService instance
 * @param {string} deviceId lane device id (scopes the lookup)
 * @param {string} id       installationImageId
 * @returns {{ src: string|null, loading: boolean, error: boolean }}
 */
export const useInstallationImage = (service, deviceId, id) => {
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!id || !deviceId) {
      return undefined;
    }

    setLoading(true);
    setError(false);
    setSrc(null);

    service
      .getImage(deviceId, id)
      .then(({ data }) => {
        if (cancelled) return;
        const encoded = data?.encodedImage;
        const contentType = data?.contentType || "image/jpeg";
        if (encoded) {
          setSrc(`data:${contentType};base64,${encoded}`);
        } else {
          setError(true);
        }
      })
      .catch(() => {
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [service, deviceId, id]);

  return { src, loading, error };
};

export default useInstallationImage;
